/**
 * Field-coverage derivations — the pivot the Fields tab is built on, with no
 * JSX so each one is unit-testable on its own.
 *
 * ── Why this is a pivot and not a validation view ────────────────────────────
 * The `/fields` endpoint returns one entry per (year, field) carrying `value`,
 * `origin`, `validation`, `reason` and `impact`. Reading the backend shows only
 * two of those five actually vary:
 *
 *   - `impact` is `_BASE_W[severity]` — a constant per severity, scaled only by
 *     the AI verdict — so every field failing one rule gets an identical
 *     number. It cannot rank fields, and the backend's own docstring says the
 *     figure belongs to a CODE across years rather than to one field.
 *   - `reason` is `_ISSUE_LABELS[code]`: the same string the Findings tab
 *     already shows, repeated once per affected field.
 *   - `origin` is effectively always `reported`; the backend refuses to guess
 *     `derived` because that would be a fabricated provenance claim. The two
 *     informative values are `absent` and `defaulted`.
 *   - `validation` is `not_reviewed` for nearly every field, because the agent
 *     digest is bounded and anything it did not reach is marked unreviewed.
 *
 * What IS unique to this endpoint is the field NAMES and the actual VALUES —
 * nothing else in the API exposes them. So the tab answers "what figures do we
 * hold, and how do they move across years", and validation is reduced to a
 * marker on the handful of cells a finding actually names.
 */

import type {
  FieldCoverageEntry,
  FieldCoverageResponse,
  FieldValidationState,
} from '../../../types/dataQuality';

/** The two states that mean a finding named this (year, field). */
const FLAGGED_STATES: FieldValidationState[] = ['failed', 'uncertain'];

/* ── the pivot ───────────────────────────────────────────────────────────── */

export interface PivotRow {
  /** Raw Neo4j property name, shown VERBATIM — matching it to the graph is
   *  the whole point of the column. Taken from the newest year that holds it,
   *  since a writer can rename a property between runs. */
  field: string;
  /** Canonical snake_case name — the join key, and the row's identity. */
  canonical: string;
  /** One entry per year, sparse: a year that never held this field has none. */
  cells: Map<number, FieldCoverageEntry>;
  /**
   * The worst human reason across every year, for the Note column.
   *
   * This is the backend's `reason`, which is the issue label — so it reads as
   * a pointer at the Findings tab rather than as independent information, and
   * it appears once per ROW instead of once per affected cell.
   */
  note: string | null;
  /** Any year's cell was named by a finding — `failed` or `uncertain`. */
  flagged: boolean;
  /**
   * Any year's cell outright FAILED, as opposed to merely `uncertain`.
   *
   * Kept separate from `flagged` so the row tint can tell an actual
   * identity-check breakage from an agent's abstention — collapsing the two
   * would paint a row red for a finding that only says "insufficient
   * evidence to judge this".
   */
  hasFailed: boolean;
  /** Any year's value is a zero standing in for a missing figure. */
  defaulted: boolean;
}

export interface PivotStatement {
  statement: string;
  label: string;
  rows: PivotRow[];
}

export interface FieldPivot {
  /** Newest first, matching the endpoint's own ordering. */
  years: number[];
  statements: PivotStatement[];
  fieldCount: number;
}

export const EMPTY_PIVOT: FieldPivot = { years: [], statements: [], fieldCount: 0 };

export function isFlagged(entry: FieldCoverageEntry): boolean {
  return FLAGGED_STATES.includes(entry.validation);
}

/**
 * Turn the year-major response into a field-major table.
 *
 * The endpoint nests years outermost because that is how it reads the graph,
 * but a reader compares one field ACROSS years — a scale jump, a year that
 * stopped being reported, a zero that appeared. That comparison is impossible
 * while each year is a separate block, which is the single biggest reason the
 * one-year-at-a-time table said so little.
 *
 * Statement order follows the backend's own `_BAG_LABELS` sequence, which is
 * the order the response already arrives in, so nothing is re-ranked here.
 */
export function buildPivot(
  response: FieldCoverageResponse | null | undefined
): FieldPivot {
  const blocks = (response?.years ?? []).filter(Boolean);
  if (blocks.length === 0) return EMPTY_PIVOT;

  const years = blocks
    .map((block) => block.year)
    .filter((year) => Number.isFinite(year))
    .sort((a, b) => b - a);

  // Insertion order preserves the backend's statement sequence.
  const statements = new Map<string, { label: string; rows: Map<string, PivotRow> }>();

  // Oldest first, so the newest year's property name wins the `field` slot.
  for (const block of [...blocks].sort((a, b) => a.year - b.year)) {
    for (const statement of block.statements ?? []) {
      if (!statement) continue;

      const bucket =
        statements.get(statement.statement) ??
        (() => {
          const created = { label: statement.label, rows: new Map<string, PivotRow>() };
          statements.set(statement.statement, created);
          return created;
        })();

      for (const entry of statement.fields ?? []) {
        if (!entry) continue;
        const key = entry.canonical || entry.field;

        const row =
          bucket.rows.get(key) ??
          (() => {
            const created: PivotRow = {
              field: entry.field,
              canonical: key,
              cells: new Map<number, FieldCoverageEntry>(),
              note: null,
              flagged: false,
              hasFailed: false,
              defaulted: false,
            };
            bucket.rows.set(key, created);
            return created;
          })();

        // Newest wins, because we iterate oldest first.
        row.field = entry.field;
        row.cells.set(block.year, entry);

        if (isFlagged(entry)) {
          row.flagged = true;
          if (entry.validation === 'failed') row.hasFailed = true;
          // A `failed` reason outranks an `uncertain` one; otherwise the first
          // reason found stands, so the note is stable across refetches.
          if (!row.note || entry.validation === 'failed') {
            row.note = entry.reason ?? row.note;
          }
        }
        if (entry.origin === 'defaulted') row.defaulted = true;
      }
    }
  }

  let fieldCount = 0;
  const out: PivotStatement[] = [];
  for (const [statement, bucket] of statements) {
    const rows = [...bucket.rows.values()];
    if (rows.length === 0) continue;
    fieldCount += rows.length;
    out.push({ statement, label: bucket.label, rows });
  }

  return { years, statements: out, fieldCount };
}

/* ── the honesty guard ───────────────────────────────────────────────────── */

/**
 * Does this payload account for the findings the chain summary reports?
 *
 * There is a known backend defect behind this. The field-coverage read follows
 * the company's direct validation edge, but a PDF chain's report hangs off its
 * `:PdfExtraction` node — so on a PDF source the stored report is not found, no
 * finding is attributed to any field, and every cell comes back unmarked.
 *
 * The payload alone CANNOT reveal that: a genuinely clean chain looks identical.
 * What reveals it is the CONTRADICTION with the chain summary — the summary
 * reports N actionable findings and this payload marks nothing. That is why the
 * finding count has to be threaded in from the summary.
 *
 * The consequence here is only a sentence, not a suppression: this tab shows
 * values and field names, which come from the graph and are unaffected. It
 * simply must not let an unmarked table imply the data was checked and cleared.
 */
export function findingsAccountedFor(args: {
  fieldCount: number;
  expectedFindingCount: number;
  flaggedRows: number;
}): boolean {
  return (
    args.fieldCount === 0 ||
    args.expectedFindingCount === 0 ||
    args.flaggedRows > 0
  );
}

export function flaggedRowCount(pivot: FieldPivot): number {
  return pivot.statements.reduce(
    (sum, statement) => sum + statement.rows.filter((row) => row.flagged).length,
    0
  );
}

/* ── search and sort ─────────────────────────────────────────────────────── */

export type FieldSort = 'graph' | 'flagged';

/** Underscores and spaces are interchangeable, so "total assets" finds `Total_Assets`. */
function normalise(text: string): string {
  return text.toLowerCase().replace(/[_\s-]+/g, ' ').trim();
}

export function matchesQuery(row: { field: string; canonical: string }, query: string): boolean {
  const needle = normalise(query);
  if (!needle) return true;
  return (
    normalise(row.field).includes(needle) || normalise(row.canonical || '').includes(needle)
  );
}

/**
 * `graph` keeps the backend's canonical-alphabetical order — the order a reader
 * matching against their own graph expects. `flagged` lifts the rows a finding
 * named to the top without otherwise disturbing that order, so the list stays
 * recognisable between the two modes.
 */
export function sortRows(rows: PivotRow[], sort: FieldSort): PivotRow[] {
  if (sort === 'graph') return rows;
  return [...rows].sort((a, b) => Number(b.flagged) - Number(a.flagged));
}

/* ── formatting ──────────────────────────────────────────────────────────── */

export interface FormattedValue {
  /** What to render. */
  display: string;
  /** The unabbreviated figure, when `display` is an abbreviation. Else null. */
  exact: string | null;
}

/** A field the payload names as a year — grouping a year is wrong under any unit. */
function isYearField(entry: { canonical: string; field: string }): boolean {
  const canonical = (entry.canonical || entry.field || '').toLowerCase();
  return canonical === 'year' || canonical.endsWith('_year');
}

/**
 * A field whose own name DECLARES it is a percentage — `ebitda_margin_pct`,
 * `direct_cost_pct`, `dataCoveragePercent`.
 *
 * This is not the currency/scale guess the rest of this function refuses to
 * make: the unit here comes from the field's own name, not from inferring
 * anything about the source. Checked against both `canonical` (snake_case)
 * and the raw `field` (which can arrive camelCase from some providers), both
 * lower-cased so the two conventions are matched the same way.
 */
function isPercentField(entry: { canonical: string; field: string }): boolean {
  const name = (entry.canonical || entry.field || '').toLowerCase();
  return /(?:^|_)pct$/.test(name) || name.endsWith('percent');
}

/**
 * A readable figure, WITHOUT inventing a unit.
 *
 * `FieldCoverageEntry` carries no `unit`, and the chains visibly disagree on
 * scale — India returns crore-scale values while a US chain returns raw USD —
 * so any currency or scale word printed here would be a guess, in a panel that
 * already refuses to name a document it cannot vouch for. What IS safe is
 * describing the number's own magnitude, so a raw 365,000,000,000 becomes
 * "365.00B" with the exact figure a hover away, while a five-digit crore value
 * is left exactly as it was.
 *
 * Grouping follows the viewer's locale deliberately: for an app serving India
 * and the US, a reader's own conventions are the right ones, and pinning a
 * single locale would render crore figures in thousands grouping.
 *
 * The real fix is a `unit` field on the payload — see the plan's Appendix A6.
 */
export function formatFieldValue(entry: FieldCoverageEntry): FormattedValue {
  const value = entry.value;
  if (value == null || !Number.isFinite(value)) return { display: '—', exact: null };

  if (isYearField(entry)) {
    return { display: String(Math.round(value)), exact: null };
  }

  if (isPercentField(entry)) {
    // A percentage never runs to the B/M magnitudes below, so no abbreviation
    // branch applies — just the grouped figure with the suffix its own name
    // already promised.
    return { display: `${value.toLocaleString(undefined, { maximumFractionDigits: 2 })}%`, exact: null };
  }

  const grouped = value.toLocaleString(undefined, { maximumFractionDigits: 2 });
  const size = Math.abs(value);

  if (size >= 1e9) return { display: `${(value / 1e9).toFixed(2)}B`, exact: grouped };
  if (size >= 1e6) return { display: `${(value / 1e6).toFixed(2)}M`, exact: grouped };
  return { display: grouped, exact: null };
}
